import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { FileText, Maximize2, Megaphone, Minimize2, TrendingUp, TrendingDown } from "lucide-react";
import { api } from "@/api/client";
import type { DashboardBrandSeries, MetricBucket, MetricPoint, ResolvedTopicWithEstimate, TopicOverviewData } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { ErrorState } from "@/components/ui/error-state";
import { Spinner } from "@/components/ui/spinner";
import { MEDIAN_COLOR, MEDIAN_KEY, TrendChart, assignTrendSeries, useTrendSeriesToggles, type SeriesMeta } from "@/components/dashboard/TrendChart";
import { TopicSummaryCard } from "@/components/topics/TopicSummaryCard";
import { LensPill, MovementGlyph, ShowMoreButton, pinnedCollapse, useShowMore } from "@/components/topics/shared";
import { useBrandsStore } from "@/stores/brands";
import { formatModelLabel } from "@/lib/format";
import { cn } from "@/lib/utils";

function fmtSqlDate(sql: string): string {
  return new Date(sql.replace(" ", "T") + "Z").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

/** `summary.as_of` is a plain UTC date key — format it without a timezone shift. */
function fmtAsOf(date: string): string {
  return new Date(date + "T00:00:00Z").toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

const num = (v: number | null | undefined, digits = 0) => (v === null || v === undefined ? "—" : v.toFixed(digits));

const GRANULARITIES: { value: MetricBucket; label: string }[] = [
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
];

const median = (vals: number[]): number | null => {
  if (vals.length === 0) return null;
  const s = [...vals].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 === 0 ? (s[m - 1]! + s[m]!) / 2 : s[m]!;
};

interface TopicOverviewTabProps {
  topicId: number;
  resolved: ResolvedTopicWithEstimate;
  onMutated: () => Promise<void>;
  onGoToTracking: () => void;
}

export function TopicOverviewTab({ topicId, resolved, onMutated, onGoToTracking }: TopicOverviewTabProps) {
  const isPulse = resolved.topic.kind === "pulse";
  const trackingEnabled = !!resolved.tracking?.enabled;

  const brands = useBrandsStore((s) => s.brands);
  const fetchBrands = useBrandsStore((s) => s.fetch);

  const [data, setData] = useState<TopicOverviewData | null>(null);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [subtopicId, setSubtopicId] = useState<number | null>(null);
  const [bucket, setBucket] = useState<MetricBucket>("week"); // trend-chart granularity; governs both lens charts
  const [tracking, setTracking] = useState<string | null>(null); // display_name currently being tracked
  const [refreshing, setRefreshing] = useState(false); // a granularity/reload refetch over already-rendered data

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  // Which lens the currently rendered `data` belongs to. Changing granularity
  // returns the same page's numbers at a different resolution, so the old chart
  // stays up (dimmed) while the new one loads; changing topic or subtopic is a
  // different page and must blank instead of showing another lens's figures.
  const lensRef = useRef<string>(`${topicId}:${subtopicId}`);

  useEffect(() => {
    let cancelled = false;
    const lens = `${topicId}:${subtopicId}`;
    if (lensRef.current !== lens) {
      lensRef.current = lens;
      setData(null);
      setRefreshing(false);
    } else {
      setRefreshing(true);
    }
    const params: { subtopic_id?: number; bucket: MetricBucket } = { bucket };
    if (subtopicId !== null) params.subtopic_id = subtopicId;
    api.topics.overview(topicId, params)
      .then((d) => { if (!cancelled) { setData(d); setError(""); } })
      .catch((e) => { if (!cancelled) setError(e?.message || "Failed to load the topic overview"); })
      .finally(() => { if (!cancelled) setRefreshing(false); });
    return () => { cancelled = true; };
  }, [topicId, subtopicId, bucket, reloadKey]);

  const refetchOverview = () => setReloadKey((k) => k + 1);

  const trackInField = async (displayName: string) => {
    setTracking(displayName);
    try {
      let brand = brands.find((b) => b.name.trim().toLowerCase() === displayName.trim().toLowerCase());
      if (!brand) brand = await api.brands.create(displayName);
      await api.topics.addCompetitor(topicId, brand.id);
      await fetchBrands();
      await onMutated();
      refetchOverview();
    } catch (e: any) {
      setError(e?.message || "Could not track this brand.");
    } finally {
      setTracking(null);
    }
  };

  if (error && !data) return <ErrorState message={error} onRetry={refetchOverview} />;
  if (!data) return <Spinner message="Loading overview…" size="lg" />;

  const subtopics = data.subtopics;

  const chips = subtopics.length > 0 ? (
    <div className="flex items-center gap-2 flex-wrap">
      {[{ id: null, name: "All" }, ...subtopics].map((s) => {
        const active = subtopicId === s.id;
        return (
          <button
            key={s.id ?? "all"}
            type="button"
            onClick={() => setSubtopicId(s.id)}
            aria-pressed={active}
            className={cn(
              "rounded-md border px-3 py-1.5 text-[12.5px] transition-colors",
              active
                ? "border-brand-600 bg-brand-50 text-brand-600 font-semibold"
                : "border-slate-300 bg-white text-slate-600 font-medium hover:border-brand-500",
            )}
          >
            {s.name}
          </button>
        );
      })}
    </div>
  ) : null;

  // Granularity chip group (aria-pressed like the subtopic chips). Governs both
  // lens trend charts; independent of the subtopic chips. Shown for arena topics.
  const granularityControl = (
    <div className={cn("flex items-center gap-2 flex-wrap transition-opacity", refreshing && "opacity-60")} aria-busy={refreshing}>
      <span className="text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-400 mr-0.5">Granularity</span>
      {GRANULARITIES.map((g) => {
        const active = bucket === g.value;
        return (
          <button
            key={g.value}
            type="button"
            onClick={() => setBucket(g.value)}
            aria-pressed={active}
            // Disabled only while a refetch is in flight — the previous chart is
            // still on screen, so a second click would just queue a race.
            disabled={refreshing}
            className={cn(
              "rounded-md border px-3 py-1.5 text-[12.5px] transition-colors disabled:cursor-not-allowed",
              active
                ? "border-brand-600 bg-brand-50 text-brand-600 font-semibold"
                : "border-slate-300 bg-white text-slate-600 font-medium hover:border-brand-500",
            )}
          >
            {g.label}
          </button>
        );
      })}
      {refreshing && <Spinner size="sm" className="ml-1 py-0 gap-0" />}
    </div>
  );

  if (!data.has_data) {
    return (
      <div className="space-y-5">
        {chips}
        <EmptyState
          icon={FileText}
          title={trackingEnabled ? "No runs yet — tracking is scheduled" : "No runs yet"}
          description={trackingEnabled
            ? "Tracking is on for this topic. Once the next scheduled run completes, its trends, blind presence, and answers show up here."
            : "Set up tracking to start collecting runs for this topic's prompts — then their trends and answers appear here."}
          action={
            <Button variant={trackingEnabled ? "secondary" : "default"} onClick={onGoToTracking}>
              {trackingEnabled ? "View tracking" : "Set up tracking"}
            </Button>
          }
        />
      </div>
    );
  }

  const primaryName = data.field?.sentiment_series.find((s) => s.is_primary)?.brand_name
    ?? brands.find((b) => b.id === resolved.primaryBrandId)?.name
    ?? "Your brand";

  // Blind lens is OFF for this topic: no blind-mode prompts AND no blind rows in
  // the window. The whole blind section gives way to one compact CTA card.
  const blindPromptCount = resolved.prompts.filter((p) => p.mode === "blind" || p.mode === "both").length;
  const blindRuns = data.blind?.leaderboard.runs ?? 0;
  const blindOff = !isPulse && blindPromptCount === 0 && blindRuns === 0;

  return (
    <div className="space-y-5">
      {chips}

      {isPulse && data.pulse
        ? <PulseSection data={data} />
        : (
          <>
            {granularityControl}
            {blindOff ? (
              <Card>
                <CardContent className="flex items-center justify-between gap-4 flex-wrap">
                  <p className="text-[12.5px] text-slate-500">
                    <span className="font-semibold text-brand-950">Blind lens is off for this topic.</span>{" "}
                    Add consumer-voice blind prompts to see who AI recommends when no brands are named.
                  </p>
                  <Link to="?tab=prompts"><Button variant="secondary" size="sm">Add blind prompts</Button></Link>
                </CardContent>
              </Card>
            ) : data.blind && (
              <BlindLensSection
                data={data}
                onTrack={trackInField}
                tracking={tracking}
                bucket={bucket}
              />
            )}
            {data.field && <FieldLensSection data={data} topicId={topicId} primaryBrandId={resolved.primaryBrandId} primaryName={primaryName} bucket={bucket} />}
          </>
        )}

      {data.recent_responses.length > 0 && <RecentAnswers responses={data.recent_responses} brandNames={brands.map((b) => b.name)} />}
    </div>
  );
}

/* ------------------------------- arena: lenses ------------------------------ */

/**
 * Prototype lens-section head: a lens pill, a title, an optional hint, and an
 * optional right-aligned stat — the lens's headline number, stated once and
 * attached to the evidence below it.
 */
function LensHead({ lens, title, hint, stat }: { lens: "blind" | "field"; title: string; hint?: string; stat?: ReactNode }) {
  return (
    <div className="flex items-baseline gap-2.5 flex-wrap">
      <LensPill lens={lens} />
      <span className="text-[14.5px] font-semibold tracking-[-0.01em] text-brand-950">{title}</span>
      {hint && <span className="text-[12px] text-slate-400">{hint}</span>}
      {stat && <span className="ml-auto flex items-baseline gap-1.5 flex-wrap">{stat}</span>}
    </div>
  );
}

/**
 * Leading plot-toggle cell for a standings row: the series swatch in its line's
 * hue while visible, a hollow ring when hidden. Rows outside the chart's series
 * cap get an empty cell rather than a dead toggle.
 */
function SeriesSwatch({ meta, visible, onToggle }: { meta: SeriesMeta | undefined; visible: boolean; onToggle: () => void }) {
  if (!meta) return <span aria-hidden="true" />;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={visible}
      title={visible ? `Hide ${meta.name} in trend` : `Show ${meta.name} in trend`}
      className="flex h-4 w-4 items-center justify-center cursor-pointer"
    >
      <span
        className={cn("w-2.5 h-2.5 rounded-full", !visible && "ring-1 ring-slate-300")}
        style={visible ? { backgroundColor: meta.color } : undefined}
        aria-hidden="true"
      />
    </button>
  );
}

/**
 * Expand/collapse control for a paired lens module: full-width mode gives the
 * chart the whole card and drops the standings key underneath it.
 */
function ExpandChartToggle({ expanded, onToggle }: { expanded: boolean; onToggle: () => void }) {
  const Icon = expanded ? Minimize2 : Maximize2;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={expanded}
      title={expanded ? "Back to split view" : "Expand chart full width"}
      className="hidden lg:inline-flex h-5 w-5 items-center justify-center rounded text-slate-400 hover:text-brand-600 shrink-0"
    >
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
    </button>
  );
}

/** The median reference line's own standings row — a dashed swatch, no value. */
function MedianToggleRow({ label, visible, onToggle, gridCols }: { label: string; visible: boolean; onToggle: () => void; gridCols: string }) {
  return (
    <div className={cn("grid items-center gap-2.5 px-2 py-1.5", gridCols)}>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={visible}
        title={visible ? `Hide ${label} in trend` : `Show ${label} in trend`}
        className="flex h-4 w-4 items-center justify-center cursor-pointer"
      >
        <svg width="12" height="4" aria-hidden="true" style={{ opacity: visible ? 1 : 0.35 }}>
          <line x1="0" y1="2" x2="12" y2="2" stroke={MEDIAN_COLOR} strokeWidth={1.5} strokeDasharray="4 3" />
        </svg>
      </button>
      <span className={cn("text-[12.5px]", visible ? "text-slate-600" : "text-slate-400")}>{label}</span>
    </div>
  );
}

/**
 * The paired blind module: the standings list IS the trend chart's legend. Each
 * row that has a line carries a swatch toggling it; the chart keeps full
 * prominence (it is the artifact users screenshot) and its PNG export.
 */
function BlindLensSection({ data, onTrack, tracking, bucket }: { data: TopicOverviewData; onTrack: (name: string) => void; tracking: string | null; bucket: MetricBucket }) {
  const leaderboard = data.blind!.leaderboard;
  const entries = leaderboard.entries;
  const likely = data.blind!.likely_queries;
  const sovTrend = data.blind!.sov_trend;
  const topShare = entries.length > 0 ? Math.max(...entries.map((e) => e.share)) : 0;
  // No previous-window data → the WoW / rank-movement columns are pure noise
  // (everything reads "new"); omit them and flag the provenance line instead.
  const firstWindow = leaderboard.prev_runs === 0;
  const gridCols = firstWindow
    ? "grid-cols-[1rem_1.75rem_minmax(120px,1.4fr)_2fr_3rem]"
    : "grid-cols-[1rem_1.75rem_minmax(120px,1.4fr)_2fr_3rem_2.5rem_2.25rem]";

  // Map each SOV series to a TrendChart brand. Untracked brands (brand_id null)
  // get stable synthetic negative ids so keying/coloring stays distinct. Null
  // points are dropped from the plotted series (positional gaps, like the field
  // chart); `latest` is the last non-null share, driving the chart's series cap.
  const chartBrands: DashboardBrandSeries[] = useMemo(() => sovTrend.series.map((s, i) => {
    const pts: MetricPoint[] = [];
    sovTrend.buckets.forEach((b, bi) => {
      const v = s.points[bi];
      if (v !== null && v !== undefined) pts.push({ bucket: b, avg: v, min: v, max: v, count: 1 });
    });
    return {
      brand_id: s.brand_id ?? -(i + 1),
      brand_name: s.display_name,
      is_primary: s.is_primary,
      latest: pts.length > 0 ? pts[pts.length - 1]!.avg : null,
      deltas: { day: null, week: null, month: null },
      series: pts,
    };
  }), [sovTrend]);

  // One bucket is a dot, not a trend: the chart cell goes quiet and the standings
  // lose their swatches rather than offering toggles with nothing to toggle.
  const hasTrend = sovTrend.buckets.length >= 2;
  const heroBrandId = chartBrands.find((b) => b.is_primary)?.brand_id ?? null;
  const metas = useMemo(
    () => (hasTrend ? assignTrendSeries(chartBrands, heroBrandId) : []),
    [chartBrands, heroBrandId, hasTrend],
  );
  // Leaderboard rows and trend series share the leaderboard's grouping key, so a
  // row can find its own line (and its hue) — or none, past the chart's cap.
  const metaByKey = useMemo(() => new Map(
    sovTrend.series.map((s, i) => [s.key, metas.find((m) => m.brandId === (s.brand_id ?? -(i + 1)))] as const),
  ), [sovTrend, metas]);
  const { hidden, toggle } = useTrendSeriesToggles(metas);
  const [fullWidth, setFullWidth] = useState(false);

  // Default to 10 leaderboard rows; keep the primary's row visible even when its
  // rank falls outside the cap (rows 1–9, a "… k more" gap, then the primary).
  const BLIND_LIMIT = 10;
  const blindMore = useShowMore(entries, BLIND_LIMIT);
  const blindCollapsed = pinnedCollapse(entries, BLIND_LIMIT, (e) => e.rank === leaderboard.primary.rank);
  const likelyMore = useShowMore(likely, 6);

  const renderLeaderboardRow = (e: (typeof entries)[number]) => {
    const meta = metaByKey.get(e.key);
    return (
      <div
        key={e.key}
        className={cn(
          "grid items-center gap-3 rounded-md px-2 py-1.5",
          gridCols,
          e.tracked && e.display_name && leaderboard.primary.rank === e.rank && "bg-brand-50",
        )}
      >
        <SeriesSwatch meta={meta} visible={!!meta && !hidden.has(meta.key)} onToggle={() => meta && toggle(meta.key)} />
        <span className="text-[12.5px] font-semibold tabular-nums text-slate-400">{e.rank}</span>
        {/* Wraps rather than squeezing: in the standings column the Untracked chip
            and its button would otherwise crush the name to nothing. */}
        <span className="flex items-center gap-2 flex-wrap min-w-0">
          <span className={cn("text-[12.5px] truncate", e.tracked ? "font-semibold text-brand-950" : "text-slate-600")}>{e.display_name}</span>
          {!e.tracked && (
            <span className="flex items-center gap-2 shrink-0">
              <span className="inline-flex items-center rounded-full border border-dashed border-slate-300 px-2 py-px text-[11px] font-medium text-slate-400 shrink-0">Untracked</span>
              <button
                type="button"
                disabled={tracking === e.display_name}
                onClick={() => onTrack(e.display_name)}
                className="text-[11px] font-semibold text-brand-600 hover:underline shrink-0 disabled:opacity-50"
              >
                {tracking === e.display_name ? "Adding…" : "Track in field"}
              </button>
            </span>
          )}
        </span>
        <span className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <span className={cn("block h-full rounded-full", e.tracked ? "bg-brand-500" : "bg-slate-300")} style={{ width: `${topShare > 0 ? (e.share / topShare) * 100 : 0}%` }} />
        </span>
        <span className="text-[12.5px] font-semibold tabular-nums text-brand-950 text-right">{(e.share * 100).toFixed(0)}%</span>
        {!firstWindow && (
          <>
            <span className="text-right text-[11.5px] font-semibold tabular-nums">
              {e.share_delta !== null && Math.abs(e.share_delta) > 0.001 ? (
                <span className={cn(e.share_delta > 0 ? "text-green-600" : "text-red-500")}>
                  {e.share_delta > 0 ? "+" : "-"}{Math.abs(e.share_delta * 100).toFixed(0)}
                </span>
              ) : <span className="text-slate-300">—</span>}
            </span>
            <span className="text-right"><MovementGlyph rank={e.rank} prevRank={e.prev_rank} /></span>
          </>
        )}
      </div>
    );
  };

  // The lens headline, stated once: where the primary lands in the blind field.
  const primaryRank = leaderboard.primary.rank;
  const primaryEntry = primaryRank !== null ? entries.find((e) => e.rank === primaryRank) ?? null : null; // entries is capped — never index by rank
  const headStat = leaderboard.runs === 0 ? null : (
    <>
      {primaryRank === null ? (
        <span className="text-[13px] font-semibold text-red-600">Not surfaced</span>
      ) : (
        <>
          <span className="text-[12px] text-slate-400">You:</span>
          <span className="text-[15px] font-bold tabular-nums text-brand-950">#{primaryRank}</span>
          <span className="text-[12px] text-slate-500">of {leaderboard.total_entries}</span>
        </>
      )}
      {!firstWindow && <MovementGlyph rank={primaryRank} prevRank={primaryEntry?.prev_rank ?? null} />}
    </>
  );

  return (
    <div className="space-y-3">
      <LensHead
        lens="blind"
        title={`Who owns ${data.topic.name} in AI answers`}
        hint="consumer-voice questions · no brands named · untracked brands included"
        stat={headStat}
      />

      <Card>
        <CardContent>
          {/* Below lg the chart leads (it's the money shot) and the standings follow;
              from lg the standings sit left as the chart's legend — unless the
              reader expands the chart full width, which drops the key underneath. */}
          <div className={cn(!fullWidth && "lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-6")}>
            <div className={cn("min-w-0", !fullWidth && "lg:order-2")}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-[11px] font-semibold tracking-[0.05em] uppercase text-slate-500">Share of AI recommendations — over time</span>
                <span className="ml-auto text-[11px] text-slate-400 tabular-nums shrink-0">% of blind visibility</span>
                {hasTrend && <ExpandChartToggle expanded={fullWidth} onToggle={() => setFullWidth((v) => !v)} />}
              </div>
              {hasTrend ? (
                <TrendChart
                  brands={chartBrands}
                  bucket={bucket}
                  heroBrandId={heroBrandId}
                  valueSuffix="%"
                  autoScale
                  title={`${data.topic.name} — share of AI recommendations`}
                  caption={`Blind lens · % of blind visibility · ${bucket}ly`}
                  exportTopic={data.topic.name}
                  exportChart="share-of-voice"
                  legend="external"
                  hiddenKeys={hidden}
                  onToggleSeries={toggle}
                  hideOverflowNote
                />
              ) : (
                <p className="py-12 text-center text-[13px] text-slate-400">No data points in this period yet.</p>
              )}
            </div>

            <div className={cn("mt-5 min-w-0", !fullWidth && "lg:mt-0 lg:order-1")}>
              {leaderboard.runs === 0 ? (
                <p className="text-[12.5px] text-slate-500">No blind runs in this window yet — first data lands after the next scheduled run.</p>
              ) : (
                <div>
                  <div className={cn("grid items-center gap-3 pb-1.5 px-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-slate-400", gridCols)}>
                    <span /><span>#</span><span>Brand</span><span /><span className="text-right">SOV</span>
                    {!firstWindow && <><span className="text-right">WoW</span><span className="text-right">Rank</span></>}
                  </div>
                  {blindMore.expanded
                    ? entries.map(renderLeaderboardRow)
                    : (
                      <>
                        {blindCollapsed.head.map(renderLeaderboardRow)}
                        {blindCollapsed.pinned && (
                          <>
                            <div className="px-2 py-1 text-[11.5px] text-slate-400">… {blindCollapsed.gap} more</div>
                            {renderLeaderboardRow(blindCollapsed.pinned)}
                          </>
                        )}
                      </>
                    )}
                  {entries.length > BLIND_LIMIT && (
                    <ShowMoreButton expanded={blindMore.expanded} count={entries.length} noun="brands" onClick={blindMore.toggle} className="mt-1.5" />
                  )}
                  <p className="px-2 pt-2 text-[11px] text-slate-400">
                    {leaderboard.runs} blind run{leaderboard.runs === 1 ? "" : "s"} · last {leaderboard.window_days} days
                    {firstWindow ? " · first month of blind data" : ""}
                  </p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {likely.length > 0 && (
        <Card>
          <CardContent className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-semibold tracking-[0.05em] uppercase text-slate-500">Most likely queries · who gets the top recommendation</span>
              <Link to="/app/blind" className="ml-auto text-[12px] font-medium text-brand-600 hover:underline shrink-0">View blind answers →</Link>
            </div>
            {likelyMore.visible.map((q) => (
              <div key={q.prompt_id} className="flex items-center gap-3 flex-wrap py-2.5 border-t border-slate-100 first:border-t-0">
                <span className="text-[12.5px] text-slate-600 flex-1 min-w-[200px]">{q.prompt_name}</span>
                {q.top && (
                  <span className="text-[12px] text-slate-500 whitespace-nowrap">
                    Top pick: <span className="font-semibold text-brand-950">{q.top.display_name}</span>
                  </span>
                )}
                {q.top === null ? (
                  <Badge variant="muted">No blind runs yet</Badge>
                ) : q.primary.mentioned ? (
                  q.primary.rank === 1
                    ? <span className="inline-flex items-center rounded-full bg-green-50 px-2.5 py-0.5 text-[11.5px] font-semibold text-green-600 tabular-nums">You #1</span>
                    : <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-[11.5px] font-semibold text-brand-600 tabular-nums">You #{q.primary.rank ?? "—"}</span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-[11.5px] font-semibold text-red-600">Not mentioned</span>
                )}
                {q.last_run_at && <span className="text-[11px] text-slate-400 tabular-nums shrink-0">{fmtSqlDate(q.last_run_at)}</span>}
              </div>
            ))}
            {likely.length > 6 && (
              <ShowMoreButton expanded={likelyMore.expanded} count={likely.length} noun="queries" onClick={likelyMore.toggle} className="mt-1" />
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * The paired field module: the tracked-brand list is the sentiment chart's
 * legend, median toggle included.
 */
function FieldLensSection({ data, topicId, primaryBrandId, primaryName, bucket }: { data: TopicOverviewData; topicId: number; primaryBrandId: number | null; primaryName: string; bucket: MetricBucket }) {
  const fieldSeries = data.field!.sentiment_series;
  const chartBrands: DashboardBrandSeries[] = useMemo(() => fieldSeries
    .filter((s) => s.series.length > 0)
    .map((s) => ({
      brand_id: s.brand_id,
      brand_name: s.brand_name,
      is_primary: s.is_primary,
      latest: s.latest,
      deltas: { day: null, week: null, month: null },
      series: s.series,
    })), [fieldSeries]);

  // Field median per bucket (competitors only), aligned to the chart's bucket
  // union. null where fewer than one competitor value exists in a bucket.
  const allBuckets = Array.from(new Set(chartBrands.flatMap((b) => b.series.map((p) => p.bucket)))).sort();
  const competitorSeries = data.field!.sentiment_series.filter((s) => !s.is_primary && s.series.length > 0);
  const medianPoints = allBuckets.map((bucket) => {
    const vals = competitorSeries
      .map((s) => s.series.find((p) => p.bucket === bucket)?.avg)
      .filter((v): v is number => v !== undefined && v !== null);
    return vals.length >= 1 ? median(vals) : null;
  });
  const medianSeries = competitorSeries.length > 0 ? { label: "Field median", points: medianPoints } : undefined;

  // The standings list is this chart's legend: same hue assignment, one shared
  // selection. Brands with no points in the window get no swatch.
  const metas = useMemo(() => assignTrendSeries(chartBrands, primaryBrandId), [chartBrands, primaryBrandId]);
  const metaByBrandId = useMemo(() => new Map(metas.map((m) => [m.brandId, m])), [metas]);
  const { hidden, toggle } = useTrendSeriesToggles(metas, medianSeries?.label);
  const [fullWidth, setFullWidth] = useState(false);
  const standingsCols = "grid-cols-[1rem_minmax(0,1fr)_3rem]";

  const competitorCount = fieldSeries.filter((s) => !s.is_primary).length;

  const summary = data.field!.summary;
  const delta = summary.delta;
  const deltaDir = delta === null ? "none" : delta > 0.05 ? "up" : delta < -0.05 ? "down" : "flat";
  const DeltaIcon = deltaDir === "up" ? TrendingUp : deltaDir === "down" ? TrendingDown : null;
  const headStat = (
    <>
      <span className="text-[18px] font-bold leading-none tracking-tight text-brand-950 tabular-nums">{num(summary.current.avg)}</span>
      {delta !== null && (
        <span className={cn(
          "inline-flex items-center gap-1 text-[13px] font-semibold tabular-nums",
          deltaDir === "up" && "text-green-600",
          deltaDir === "down" && "text-red-500",
          (deltaDir === "flat" || deltaDir === "none") && "text-slate-400",
        )}>
          {DeltaIcon && <DeltaIcon className="w-3 h-3" />}
          {delta > 0 ? "+" : ""}{delta.toFixed(1)} WoW
        </span>
      )}
      {summary.is_stale && summary.as_of && <span className="text-[11px] text-slate-400">as of {fmtAsOf(summary.as_of)}</span>}
    </>
  );

  const FIELD_SOV_LIMIT = 8;
  const fieldSov = (data.field!.share_of_voice ?? []).filter((e) => e.share > 0);
  const topSov = fieldSov.length > 0 ? Math.max(...fieldSov.map((e) => e.share)) : 0;
  const sovMore = useShowMore(fieldSov, FIELD_SOV_LIMIT);
  const sovCollapsed = pinnedCollapse(fieldSov, FIELD_SOV_LIMIT, (e) => e.brand_id === primaryBrandId);
  const renderFieldSovRow = (e: (typeof fieldSov)[number]) => (
    <div key={e.brand_name} className="grid grid-cols-[minmax(90px,118px)_minmax(0,1fr)_2.75rem] items-center gap-2.5">
      <span className={cn("text-[12.5px] truncate", e.tracked && e.brand_id === primaryBrandId ? "font-bold text-brand-950" : "text-slate-600")}>{e.brand_name}</span>
      <span className="h-2 rounded-full bg-slate-100 overflow-hidden">
        <span className={cn("block h-full rounded-full", e.brand_id === primaryBrandId ? "bg-brand-500" : "bg-slate-300")} style={{ width: `${topSov > 0 ? (e.share / topSov) * 100 : 0}%` }} />
      </span>
      <span className="text-[12.5px] font-semibold tabular-nums text-brand-950 text-right">{e.share.toFixed(0)}%</span>
    </div>
  );
  return (
    <div className="space-y-3">
      <LensHead
        lens="field"
        title={`${primaryName} vs your chosen field`}
        hint={competitorCount === 0
          ? "scored prompts · no tracked competitors yet"
          : `scored prompts · ${competitorCount} tracked competitor${competitorCount === 1 ? "" : "s"}`}
        stat={headStat}
      />

      {chartBrands.length > 0 && (
        <Card>
          <CardContent>
            <div className={cn(!fullWidth && "lg:grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-6")}>
              <div className={cn("min-w-0", !fullWidth && "lg:order-2")}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[11px] font-semibold tracking-[0.05em] uppercase text-slate-500">Sentiment trend</span>
                  <span className="ml-auto text-[11px] text-slate-400 shrink-0">Asked about your brand directly</span>
                  <ExpandChartToggle expanded={fullWidth} onToggle={() => setFullWidth((v) => !v)} />
                </div>
                <TrendChart
                  brands={chartBrands}
                  bucket={bucket}
                  heroBrandId={primaryBrandId}
                  medianSeries={medianSeries}
                  title={`${data.topic.name} — sentiment trend`}
                  caption={`Field lens · asked about your brand directly · ${bucket}ly`}
                  exportTopic={data.topic.name}
                  exportChart="sentiment"
                  legend="external"
                  hiddenKeys={hidden}
                  onToggleSeries={toggle}
                  hideOverflowNote
                />
              </div>

              <div className={cn("mt-5 min-w-0", !fullWidth && "lg:mt-0 lg:order-1")}>
                <div className={cn("grid items-center gap-2.5 pb-1.5 px-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-slate-400", standingsCols)}>
                  <span /><span>Brand</span><span className="text-right">Latest</span>
                </div>
                {fieldSeries.map((s) => {
                  const meta = metaByBrandId.get(s.brand_id);
                  return (
                    <div key={s.brand_id} className={cn("grid items-center gap-2.5 rounded-md px-2 py-1.5", standingsCols, s.is_primary && "bg-brand-50")}>
                      <SeriesSwatch meta={meta} visible={!!meta && !hidden.has(meta.key)} onToggle={() => meta && toggle(meta.key)} />
                      <span className={cn("text-[12.5px] truncate", s.is_primary ? "font-semibold text-brand-950" : "text-slate-600")}>{s.brand_name}</span>
                      <span className="text-[12.5px] font-semibold tabular-nums text-brand-950 text-right">{num(s.latest)}</span>
                    </div>
                  );
                })}
                {medianSeries && (
                  <MedianToggleRow
                    label={medianSeries.label}
                    visible={!hidden.has(MEDIAN_KEY)}
                    onToggle={() => toggle(MEDIAN_KEY)}
                    gridCols={standingsCols}
                  />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {(fieldSov.length > 0 || data.field!.by_model.length > 0) && (
        <div className="grid gap-4 lg:grid-cols-2">
          {fieldSov.length > 0 && (
            <Card>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold tracking-[0.05em] uppercase text-slate-500">Field share of voice · this week</span>
                  <span className="ml-auto text-[11px] text-slate-400 tabular-nums">% of scored answers</span>
                </div>
                <div className="space-y-1.5">
                  {sovMore.expanded
                    ? fieldSov.map(renderFieldSovRow)
                    : (
                      <>
                        {sovCollapsed.head.map(renderFieldSovRow)}
                        {sovCollapsed.pinned && (
                          <>
                            <div className="px-0.5 text-[11.5px] text-slate-400">… {sovCollapsed.gap} more</div>
                            {renderFieldSovRow(sovCollapsed.pinned)}
                          </>
                        )}
                      </>
                    )}
                </div>
                {fieldSov.length > FIELD_SOV_LIMIT && (
                  <ShowMoreButton expanded={sovMore.expanded} count={fieldSov.length} noun="brands" onClick={sovMore.toggle} />
                )}
              </CardContent>
            </Card>
          )}

          {data.field!.by_model.length > 0 && (
            <Card>
              <CardContent className="space-y-3">
                <span className="text-[11px] font-semibold tracking-[0.05em] uppercase text-slate-500">{primaryName} sentiment by model</span>
                <div className="grid gap-3">
                  {data.field!.by_model.map((m) => {
                    const dir = m.delta === null ? "flat" : m.delta > 0.05 ? "up" : m.delta < -0.05 ? "down" : "flat";
                    return (
                      <div key={m.model} className="rounded-lg border border-slate-200 px-3.5 py-3">
                        <div className="text-[12px] font-semibold text-slate-600 mb-1.5 truncate">{formatModelLabel(m.model)}</div>
                        <span className="text-[20px] font-bold text-brand-950 tabular-nums mr-2">{num(m.avg_sentiment, 1)}</span>
                        {m.delta !== null && (
                          <span className={cn(
                            "text-[12px] font-semibold tabular-nums",
                            dir === "up" && "text-green-600",
                            dir === "down" && "text-red-500",
                            dir === "flat" && "text-slate-400",
                          )}>
                            {dir === "up" ? "+" : ""}{m.delta.toFixed(1)} WoW
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      <TopicSummaryCard topicId={topicId} hasRuns={data.recent_responses.length > 0} latestComparisonId={data.recent_responses[0]?.comparison_id ?? null} />
    </div>
  );
}

/* ---------------------------------- pulse ----------------------------------- */

function PulseSection({ data }: { data: TopicOverviewData }) {
  const pulse = data.pulse!;
  const current = pulse.summary.current.avg;
  const delta = pulse.summary.delta;
  const deltaDir = delta === null ? "none" : delta > 0.05 ? "up" : delta < -0.05 ? "down" : "flat";
  const DeltaIcon = deltaDir === "up" ? TrendingUp : deltaDir === "down" ? TrendingDown : null;

  const chartBrands: DashboardBrandSeries[] = pulse.series.length > 0 ? [{
    brand_id: 0,
    brand_name: "Sentiment",
    is_primary: true,
    latest: current,
    deltas: { day: null, week: null, month: delta },
    series: pulse.series,
  }] : [];

  return (
    <>
      <Card className="bg-brand-50/40">
        <CardContent className="space-y-1.5">
          <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Brand pulse sentiment</span>
          <div className="flex items-end gap-2">
            <span className="text-[44px] font-bold leading-none text-brand-700 tabular-nums">{num(current)}</span>
            {delta !== null && (
              <span className={cn(
                "mb-1.5 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[12px] font-semibold tabular-nums",
                deltaDir === "up" && "bg-green-50 text-green-600",
                deltaDir === "down" && "bg-red-50 text-red-500",
                (deltaDir === "flat" || deltaDir === "none") && "bg-slate-100 text-slate-500",
              )}>
                {DeltaIcon && <DeltaIcon className="w-3 h-3" />}
                {delta > 0 ? "+" : ""}{delta.toFixed(1)}
              </span>
            )}
          </div>
          <p className="text-[12px] text-slate-500">How AI talks about your brand on its own — no competitors.</p>
          {pulse.summary.is_stale && pulse.summary.as_of && (
            <p className="text-[11px] text-slate-400">as of {fmtAsOf(pulse.summary.as_of)}</p>
          )}
        </CardContent>
      </Card>

      {chartBrands.length > 0 && (
        <Card>
          <CardContent>
            <div className="flex items-center justify-between mb-3">
              <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">Sentiment — over time</span>
            </div>
            <TrendChart
              brands={chartBrands}
              bucket="week"
              heroBrandId={0}
              title={`${data.topic.name} — sentiment over time`}
              caption="Brand pulse · no competitor field · weekly"
              exportTopic={data.topic.name}
              exportChart="pulse-sentiment"
            />
          </CardContent>
        </Card>
      )}

      {pulse.per_prompt.length > 0 && (
        <Card>
          <CardContent className="space-y-1">
            <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-slate-500">By question</span>
            {pulse.per_prompt.map((p) => (
              <div key={p.prompt_id} className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
                <span className="text-[13px] font-medium text-slate-700 truncate">{p.prompt_name}</span>
                <span className="ml-auto flex items-center gap-2 shrink-0 text-[12px] tabular-nums">
                  <span className="font-semibold text-slate-600">{num(p.avg_sentiment)}</span>
                  {p.delta !== null && (
                    <span className={cn("font-semibold", p.delta > 0.05 ? "text-green-600" : p.delta < -0.05 ? "text-red-500" : "text-slate-400")}>
                      {p.delta > 0 ? "+" : ""}{p.delta.toFixed(1)}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </>
  );
}

/* ------------------------------ recent answers ------------------------------ */

/** Highlight tracked brand names in an excerpt (case-insensitive, whole-word). */
function highlightBrands(text: string, brandNames: string[]) {
  const names = brandNames.filter((n) => n.trim().length > 1).sort((a, b) => b.length - a.length);
  if (names.length === 0) return [text];
  const escaped = names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "gi");
  const parts = text.split(re);
  return parts.map((part, i) =>
    names.some((n) => n.toLowerCase() === part.toLowerCase())
      ? <b key={i} className="font-semibold not-italic text-slate-700">{part}</b>
      : part,
  );
}

function RecentAnswers({ responses, brandNames }: { responses: TopicOverviewData["recent_responses"]; brandNames: string[] }) {
  return (
    <Card>
      <CardContent className="space-y-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[11px] font-semibold tracking-[0.05em] uppercase text-slate-500">Recent answers · both lenses</span>
        </div>
        {responses.map((r) => (
          <div key={r.comparison_id} className="py-3 border-t border-slate-100 first:border-t-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-[11.5px] font-semibold text-brand-600">{formatModelLabel(r.model)}</span>
              <LensPill lens={r.query_mode === "blind_query" ? "blind" : "field"} />
              {r.winner && <span className="text-[12px] text-slate-400">top brand: <span className="font-medium text-slate-500">{r.winner}</span></span>}
              <span className="ml-auto text-[11.5px] text-slate-400 tabular-nums">{fmtSqlDate(r.created_at)}</span>
              <Link to={`/app/compare/results/${r.comparison_id}`} className="text-[12px] font-medium text-brand-600 hover:underline shrink-0">Full answer →</Link>
            </div>
            {r.excerpt && (
              <p className="text-[12.5px] leading-relaxed italic text-slate-400 line-clamp-4">
                “{highlightBrands(r.excerpt, brandNames)}”
              </p>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
